import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SpreadsheetDocument } from '../../models/SpreadsheetDocument';
import type { SiteNetworkDevice } from '../../models/SiteNetworkDevice';
import { SiteNetworkDeviceService } from '../../services/SiteNetworkDeviceService';
import {
  NvrRegisterService,
  NvrRegisterResult,
  NvrRegisterStatus,
} from '../../services/NvrRegisterService';
import { documentToFocusAiCameras, FocusAiCamera } from '../../services/nvrTemplateExport';

const CATEGORY_INTEGRATION_SERVER = 'integration-server';
const CATEGORY_NVR = 'nvr';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

const STATUS_LABEL: Record<NvrRegisterStatus, { label: string; color: ChipColor }> = {
  planned: { label: '등록 예정', color: 'info' },
  ok: { label: '등록됨', color: 'success' },
  check: { label: '확인 필요', color: 'warning' },
  skip: { label: '건너뜀', color: 'default' },
  fail: { label: '실패', color: 'error' },
};

interface NvrRemoteRegisterDialogProps {
  projectId: number;
  /** 포커스AI 형식 문서. null 이면 닫힌 상태. */
  doc: SpreadsheetDocument | null;
  onClose: () => void;
}

interface Selection {
  serverId: number | '';
  nvrId: number | '';
  user: string;
  password: string;
}

function camerasOf(doc: SpreadsheetDocument | null): { cameras: FocusAiCamera[]; error: string } {
  if (!doc) return { cameras: [], error: '' };
  try {
    return { cameras: documentToFocusAiCameras(doc), error: '' };
  } catch (e) {
    return { cameras: [], error: e instanceof Error ? e.message : '문서를 읽지 못했습니다' };
  }
}

/**
 * 포커스AI CSV 로 올린 구역 문서를 그 구역 녹화기에 원격 등록한다. 미리보기로 녹화기의 현재 채널과
 * 검색 결과를 대조한 뒤 [등록]을 누른다 — 같은 IP 를 다시 등록하면 녹화기가 채널을 하나 더 만들어서
 * 이미 있는 카메라·차 있는 채널은 서버가 건너뛴다.
 */
export default function NvrRemoteRegisterDialog({ projectId, doc, onClose }: NvrRemoteRegisterDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [devices, setDevices] = useState<SiteNetworkDevice[]>([]);
  const [sel, setSel] = useState<Selection>({ serverId: '', nvrId: '', user: 'admin', password: '' });
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [result, setResult] = useState<NvrRegisterResult | null>(null);
  const [previewKey, setPreviewKey] = useState('');
  const [error, setError] = useState('');
  const { cameras, error: docError } = useMemo(() => camerasOf(doc), [doc]);

  useEffect(() => {
    if (!doc) return undefined;
    let alive = true;
    setResult(null);
    setPreviewKey('');
    setError('');
    SiteNetworkDeviceService.list(projectId)
      .then((list) => {
        if (!alive) return;
        setDevices(list);
        const server = list.find((d) => d.category === CATEGORY_INTEGRATION_SERVER);
        setSel((s) => ({ ...s, serverId: server?.id ?? '' }));
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : '현장 장비를 읽지 못했습니다');
      });
    return () => {
      alive = false;
    };
  }, [doc, projectId]);

  const servers = devices.filter((d) => d.category === CATEGORY_INTEGRATION_SERVER);
  const nvrs = devices.filter((d) => d.category === CATEGORY_NVR);
  const selKey = `${sel.serverId}/${sel.nvrId}/${sel.user}/${sel.password}`;
  const planned =
    result && !result.apply ? result.items.filter((i) => i.status === 'planned').length : 0;
  const ready =
    sel.serverId !== '' &&
    sel.nvrId !== '' &&
    sel.user.trim() !== '' &&
    sel.password !== '' &&
    cameras.length > 0;

  const pickNvr = (id: number) => {
    const nvr = nvrs.find((d) => d.id === id);
    // 녹화기 장비에 웹 계정이 적혀 있으면 채운다(없으면 입력한 값 유지).
    setSel((s) => ({
      ...s,
      nvrId: id,
      user: nvr?.username || s.user,
      password: nvr?.ssh_password || s.password,
    }));
  };

  const run = async (apply: boolean) => {
    if (sel.serverId === '' || sel.nvrId === '') return;
    if (apply) {
      const nvr = nvrs.find((d) => d.id === sel.nvrId);
      if (!window.confirm(`${nvr?.name}(${nvr?.ip_address})에 ${planned}대를 등록합니다. 진행할까요?`)) {
        return;
      }
    }
    setBusy(apply ? 'apply' : 'preview');
    setError('');
    try {
      const res = await NvrRegisterService.register(projectId, {
        serverDeviceId: sel.serverId,
        nvrDeviceId: sel.nvrId,
        nvrUser: sel.user.trim(),
        nvrPassword: sel.password,
        apply,
        cameras,
      });
      setResult(res);
      setPreviewKey(apply ? '' : selKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'NVR 원격 등록 실패');
    } finally {
      setBusy(null);
    }
  };

  const counts = (result?.items ?? []).reduce<Record<string, number>>((acc, i) => {
    acc[i.status] = (acc[i.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog
      open={!!doc}
      onClose={busy ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="nvr-register-title"
    >
      <DialogTitle id="nvr-register-title">NVR 원격 등록 — {doc?.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            통합서버를 거쳐 녹화기의 카메라 검색 결과로 등록합니다(웹 [검색 등록]과 같음). 검색에 20초,
            등록·확인에 대수만큼 더 걸립니다. 이미 있는 카메라·차 있는 채널은 건너뛰고, 채널 이름(title)은
            녹화기에 들어가지 않습니다.
          </Typography>
          {docError && <Alert severity="error">{docError}</Alert>}
          {servers.length === 0 && devices.length > 0 && (
            <Alert severity="warning">이 현장 네트워크 장비에 통합서버(integration-server)가 없습니다.</Alert>
          )}
          {nvrs.length === 0 && devices.length > 0 && (
            <Alert severity="warning">
              이 현장 네트워크 장비에 NVR 이 없습니다 — 네트워크 장비에 녹화기를 먼저 등록하세요.
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="통합서버"
              value={sel.serverId}
              onChange={(e) => setSel((s) => ({ ...s, serverId: Number(e.target.value) }))}
              fullWidth
            >
              {servers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name} ({d.ip_address})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="녹화기(NVR)"
              value={sel.nvrId}
              onChange={(e) => pickNvr(Number(e.target.value))}
              fullWidth
            >
              {nvrs.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name} ({d.ip_address})
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="녹화기 웹 아이디"
              value={sel.user}
              onChange={(e) => setSel((s) => ({ ...s, user: e.target.value }))}
              fullWidth
              autoComplete="off"
            />
            <TextField
              label="녹화기 웹 비밀번호"
              type="password"
              value={sel.password}
              onChange={(e) => setSel((s) => ({ ...s, password: e.target.value }))}
              fullWidth
              autoComplete="new-password"
            />
          </Stack>
          <Typography variant="body2">카메라 {cameras.length}대 (문서에서 ip 가 있는 행)</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  size="small"
                  label={result.apply ? '등록 결과' : '미리보기'}
                  color={result.apply ? 'primary' : 'default'}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`녹화기 ${result.channelCount}채널 · 검색 ${result.discovered}대`}
                />
                {(Object.keys(STATUS_LABEL) as NvrRegisterStatus[])
                  .filter((s) => counts[s])
                  .map((s) => (
                    <Chip
                      key={s}
                      size="small"
                      color={STATUS_LABEL[s].color}
                      label={`${STATUS_LABEL[s].label} ${counts[s]}`}
                    />
                  ))}
              </Stack>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }} aria-label="등록 결과">
                {result.items.map((it) => (
                  <Box
                    component="li"
                    key={`${it.channel}-${it.ip}`}
                    sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', overflowWrap: 'anywhere' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Chip
                        size="small"
                        color={STATUS_LABEL[it.status].color}
                        label={STATUS_LABEL[it.status].label}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        CH{it.channel}
                      </Typography>
                      <Typography variant="body2">{it.ip}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {it.title}
                      </Typography>
                      {result.apply && (it.status === 'ok' || it.status === 'check') && (
                        <Typography variant="caption" color="text.secondary">
                          메인 {it.main ? 'O' : 'X'} · 서브 {it.sub ? 'O' : 'X'}
                        </Typography>
                      )}
                    </Stack>
                    {it.reason && (
                      <Typography variant="caption" color="text.secondary">
                        {it.reason}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={!!busy}>
          닫기
        </Button>
        <Button
          variant="outlined"
          onClick={() => run(false)}
          disabled={!ready || !!busy}
          startIcon={busy === 'preview' ? <CircularProgress size={14} /> : undefined}
        >
          {busy === 'preview' ? '녹화기 확인 중…' : '미리보기'}
        </Button>
        <Button
          variant="contained"
          onClick={() => run(true)}
          disabled={!ready || !!busy || planned === 0 || previewKey !== selKey}
          startIcon={busy === 'apply' ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {busy === 'apply' ? '등록 중…' : `등록${planned ? ` (${planned}대)` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
